/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-element-interactions, react/no-unescaped-entities */
import React from 'react'

import DOMPurify from 'dompurify'
import moment from 'moment'

import format from '../../../../services/format'
import { formatPhone } from '../../../../utils/PhoneUtils'
import FAIcon from '../../../FAIcon'

import ReportText from './ReportText'

const EDITABLE_STATUSES = new Set([
  'in-progress',
  'inprogress',
  'draft',
  'submitted',
  'sent-to-administrator',
  'in-review',
  'under-review',
  'in-smart-review',
  'rejected',
])

export default function InteractiveReportPreview({
  caseReportModel,
  caseManager,
  referralSource,
  adjuster,
  settings,
  onFieldClick,
  activeFieldId,
  allCaseAppointments = [],
  isReadOnly: isReadOnlyProp = false,
}) {
  const caseData = caseReportModel?.case || {}
  const statusText = (caseReportModel?.status || '').toLowerCase().replace(/_/g, '-').replace(/\s+/g, '-')
  const isReadOnly = Boolean(isReadOnlyProp) || !EDITABLE_STATUSES.has(statusText)
  const isInitialReport = (caseReportModel?.reportType || '').toLowerCase() === 'initial'
  const allDiagnosis = caseData.diagnosis ?? []
  const allMedications = caseData.medications ?? []
  const allAllergies = caseData.allergies ?? []
  const allDiagnosticTests = caseData.tests ?? caseData.diagnosticTests ?? []
  const client = caseData.client || {}
  const referral = caseData.referral || {}
  const employer = caseData.employer || {}
  const status = caseData.status || {}
  const allTransportation = caseReportModel?.transportation || []
  const allTranslation = caseReportModel?.translationServices || []
  const respondentIndividual = `${caseData.defendant?.contact?.firstName || ''} ${
    caseData.defendant?.contact?.lastName || ''
  }`.trim()
  const claimantIndividual = `${caseData.plaintiff?.contact?.firstName || ''} ${
    caseData.plaintiff?.contact?.lastName || ''
  }`.trim()

  const rawInjuryDescriptions = caseData.injury?.descriptions || []
  const allInjuryDescriptions = rawInjuryDescriptions.filter(
    item => item && item.description && item.description.replace(/<[^>]*>/g, '').trim(),
  )

  const hasValidFrom =
    moment.utc(caseReportModel?.fromDateTimeUtc).isValid() && moment.utc(caseReportModel?.fromDateTimeUtc).year() > 1900
  const hasValidTo =
    moment.utc(caseReportModel?.toDateTimeUtc).isValid() && moment.utc(caseReportModel?.toDateTimeUtc).year() > 1900
  const fromDate = hasValidFrom ? moment.utc(caseReportModel?.fromDateTimeUtc).startOf('day') : null
  const toDate = hasValidTo ? moment.utc(caseReportModel?.toDateTimeUtc).endOf('day') : null

  const getItemizedRecords = () => {
    if (!caseReportModel) return []
    const records = []

    const activityToApptLookup = { ...(caseReportModel.activityToApptLookup || {}) }
    const activityToTaskLookup = { ...(caseReportModel.activityToTaskLookup || {}) }
    const appointmentLookup = {}

    if (Array.isArray(allCaseAppointments)) {
      allCaseAppointments.forEach(appt => {
        if (appt && appt.appointmentNumber != null) {
          appointmentLookup[appt.appointmentNumber] = appt
          if (Array.isArray(appt.activities)) {
            appt.activities.forEach(act => {
              if (act && act.activityNumber) {
                activityToApptLookup[act.activityNumber] = appt.appointmentNumber
              }
            })
          }
        }
      })
    }
    if (Array.isArray(caseReportModel.upcomingAppointments)) {
      caseReportModel.upcomingAppointments.forEach(appt => {
        if (appt && appt.appointmentNumber != null) {
          appointmentLookup[appt.appointmentNumber] = {
            ...appointmentLookup[appt.appointmentNumber],
            ...appt,
          }
        }
      })
    }
    if (Array.isArray(caseReportModel.appointments)) {
      caseReportModel.appointments.forEach(appt => {
        if (appt && appt.appointmentNumber != null) {
          appointmentLookup[appt.appointmentNumber] = {
            ...appointmentLookup[appt.appointmentNumber],
            ...appt,
          }
        }
      })
    }
    const taskLookup = {}
    if (Array.isArray(caseReportModel.tasks)) {
      caseReportModel.tasks.forEach(t => {
        if (t && t.taskNumber != null) {
          taskLookup[t.taskNumber] = t
        }
      })
    }

    if (caseReportModel.activities) {
      caseReportModel.activities.forEach(x => {
        const hours = x.time || 0
        if (hours > 0 || x.activityNumber != null) {
          let apptNum = x.appointmentNumber
          if (!apptNum && activityToApptLookup[x.activityNumber]) {
            apptNum = activityToApptLookup[x.activityNumber]
          }
          let taskNum = x.taskNumber
          if (!taskNum && activityToTaskLookup[x.activityNumber]) {
            taskNum = activityToTaskLookup[x.activityNumber]
          }

          const parentType = apptNum ? 'Appointment' : taskNum ? 'Task' : null
          const parentId = apptNum || taskNum
          let parentTitle = ''
          let parentDate = x.date
          if (apptNum) {
            const appt = appointmentLookup[apptNum]
            if (appt) {
              parentTitle = appt.title || appt.eventTitle || `Appointment #${apptNum}`
              parentDate = appt.date || appt.startUtc || x.date
            } else {
              parentTitle = `Appointment #${apptNum}`
              parentDate = x.date
            }
          } else if (taskNum) {
            const task = taskLookup[taskNum]
            if (task) {
              parentTitle = task.title || `Task #${taskNum}`
              parentDate = task.date || task.dueDateUtc || x.date
            } else {
              parentTitle = `Task #${taskNum}`
              parentDate = x.date
            }
          }

          const rateUsed =
            x.rateUsed ||
            (hours > 0 && (x.calculatedAmount || 0) > 0
              ? Math.round(((x.calculatedAmount || 0) / hours) * 100) / 100
              : 0)

          records.push({
            type: 'activity',
            recordId: x.activityNumber,
            date: x.date,
            description: x.description || x.type,
            hours,
            miles: 0,
            expenseAmount: 0,
            rateUsed,
            totalCharge: x.calculatedAmount || 0,
            parentType,
            parentId,
            parentTitle,
            parentDate,
            appointment: apptNum ? appointmentLookup[apptNum] : null,
            task: taskNum ? taskLookup[taskNum] : null,
            sortOrder: x.sortOrder,
            originalItem: x,
          })
        }
      })
    }

    if (caseReportModel.appointments) {
      caseReportModel.appointments.forEach(appt => {
        if (appt.expenses) {
          appt.expenses.forEach(x => {
            records.push({
              type: 'expense',
              recordId: x.expenseNumber,
              parentType: 'Appointment',
              parentId: appt.appointmentNumber,
              parentTitle: appt.title,
              parentDate: appt.startUtc || x.date,
              date: x.date,
              description: x.description || x.type,
              hours: 0,
              miles: x.distance || 0,
              expenseAmount: x.amount || 0,
              rateUsed: x.rateUsed || 0,
              totalCharge: x.calculatedAmount || 0,
              appointment: appt,
              originalItem: x,
            })
          })
        }
      })
    }

    if (caseReportModel.tasks) {
      caseReportModel.tasks.forEach(task => {
        if (task.expenses) {
          task.expenses.forEach(x => {
            records.push({
              type: 'expense',
              recordId: x.expenseNumber,
              parentType: 'Task',
              parentId: task.taskNumber,
              parentTitle: task.title,
              parentDate: task.dueDateUtc || task.createdUtc || x.date,
              date: x.date,
              description: x.description || x.type,
              hours: 0,
              miles: x.distance || 0,
              expenseAmount: x.amount || 0,
              rateUsed: x.rateUsed || 0,
              totalCharge: x.calculatedAmount || 0,
              task,
              originalItem: x,
            })
          })
        }
      })
    }

    const activeApptExpenseIds = new Set()
    const activeTaskExpenseIds = new Set()
    if (caseReportModel.appointments) {
      caseReportModel.appointments.forEach(appt => {
        if (appt.expenses) {
          appt.expenses.forEach(e => activeApptExpenseIds.add(e.expenseNumber))
        }
      })
    }
    if (caseReportModel.tasks) {
      caseReportModel.tasks.forEach(t => {
        if (t.expenses) {
          t.expenses.forEach(e => activeTaskExpenseIds.add(e.expenseNumber))
        }
      })
    }

    const expenseToApptLookup = caseReportModel.expenseToApptLookup || {}
    const expenseToTaskLookup = caseReportModel.expenseToTaskLookup || {}

    if (caseReportModel.miscExpenses) {
      caseReportModel.miscExpenses.forEach(x => {
        if (!activeApptExpenseIds.has(x.expenseNumber) && !activeTaskExpenseIds.has(x.expenseNumber)) {
          let apptNum = x.appointmentNumber
          if (!apptNum && expenseToApptLookup[x.expenseNumber]) {
            apptNum = expenseToApptLookup[x.expenseNumber]
          }
          let taskNum = x.taskNumber
          if (!taskNum && expenseToTaskLookup[x.expenseNumber]) {
            taskNum = expenseToTaskLookup[x.expenseNumber]
          }

          const parentType = apptNum ? 'Appointment' : taskNum ? 'Task' : null
          const parentId = apptNum || taskNum
          let parentTitle = ''
          let parentDate = x.date
          if (apptNum) {
            const appt = appointmentLookup[apptNum]
            if (appt) {
              parentTitle = appt.title || appt.eventTitle || `Appointment #${apptNum}`
              parentDate = appt.date || appt.startUtc || x.date
            } else {
              parentTitle = `Appointment #${apptNum}`
              parentDate = x.date
            }
          } else if (taskNum) {
            const task = taskLookup[taskNum]
            if (task) {
              parentTitle = task.title || `Task #${taskNum}`
              parentDate = task.date || task.dueDateUtc || x.date
            } else {
              parentTitle = `Task #${taskNum}`
              parentDate = x.date
            }
          }

          records.push({
            type: 'misc-expense',
            recordId: x.expenseNumber,
            date: x.date,
            description: x.description || 'Miscellaneous Expense',
            hours: 0,
            miles: x.distance || 0,
            expenseAmount: x.amount || 0,
            rateUsed: x.rateUsed || 0,
            totalCharge: x.calculatedAmount || 0,
            parentType,
            parentId,
            parentTitle,
            parentDate,
            appointment: apptNum ? appointmentLookup[apptNum] : null,
            task: taskNum ? taskLookup[taskNum] : null,
            originalItem: x,
          })
        }
      })
    }

    records.forEach(ir => {
      if (!ir.parentType) {
        ir.parentType = null
        ir.parentId = null
        ir.parentTitle = 'General Activities'
        ir.parentDate = '9999-12-31'
      }
    })

    return records
  }

  const itemizedRecords = getItemizedRecords()

  const groups = {}
  itemizedRecords.forEach(ir => {
    const key = `${ir.parentType || 'none'}_${ir.parentId || 'none'}_${ir.parentTitle}`
    if (!groups[key]) {
      groups[key] = {
        parentType: ir.parentType,
        parentId: ir.parentId,
        parentTitle: ir.parentTitle,
        parentDate: ir.parentDate,
        appointment: ir.appointment || null,
        task: ir.task || null,
        items: [],
      }
    }
    groups[key].items.push(ir)
  })

  const sortedGroups = Object.values(groups).sort((a, b) => {
    if (a.parentDate === '9999-12-31') return 1
    if (b.parentDate === '9999-12-31') return -1
    return new Date(a.parentDate) - new Date(b.parentDate)
  })

  const roundTwoDecimals = val => Math.round(((val || 0) + Number.EPSILON) * 100) / 100

  const totalHours = roundTwoDecimals(itemizedRecords.reduce((sum, x) => sum + (x.hours || 0), 0))
  const totalMiles = roundTwoDecimals(itemizedRecords.reduce((sum, x) => sum + (x.miles || 0), 0))
  const totalExpenses = roundTwoDecimals(itemizedRecords.reduce((sum, x) => sum + (x.expenseAmount || 0), 0))

  const treatmentProviders =
    caseReportModel?.case?.treatmentProviders ||
    caseReportModel?.treatmentProviders ||
    caseData?.treatmentProviders ||
    []
  const handleEditClick = (fieldKey, fieldType, fieldTitle, currentValue) => {
    if (isReadOnly) return
    onFieldClick(fieldKey, fieldType, fieldTitle, currentValue)
  }

  const getReferralTypeDisplay = type => {
    if (!type) return null
    if (type === 'FCM') return 'FCM (Field Case Management)'
    if (type === 'TCM') return 'TCM (Telephonic Case Management)'
    if (type === 'Task') return 'Task'
    return type
  }

  const clientName = client ? `${client.firstName} ${client.lastName}` : ''
  const reportType = caseReportModel?.reportType
  const reportTitle =
    reportType === 'Final'
      ? 'Case Management Final Report'
      : reportType === 'Initial'
      ? 'Case Management Initial Report'
      : reportType === 'CaseManagement'
      ? 'Case Management Report'
      : `Case Management Progress Report ${caseReportModel?.sequenceNumber || 1}`

  const dateText =
    hasValidFrom && hasValidTo
      ? `${moment.utc(caseReportModel?.fromDateTimeUtc).format('MM/DD/YYYY')} - ${moment
          .utc(caseReportModel?.toDateTimeUtc)
          .format('MM/DD/YYYY')}`
      : hasValidFrom
      ? moment.utc(caseReportModel?.fromDateTimeUtc).format('MM/DD/YYYY')
      : hasValidTo
      ? moment.utc(caseReportModel?.toDateTimeUtc).format('MM/DD/YYYY')
      : ''

  // MMI info
  const latestMMI = caseData.mmi?.history?.slice().sort((a, b) => new Date(b.date) - new Date(a.date))[0] || null

  return (
    <div
      className={`report interactive-report-preview bg-white p-5 border rounded shadow-sm ${
        isReadOnly ? 'is-readonly' : ''
      }`}
      style={{ fontFamily: "Calibri, 'Segoe UI', Arial, sans-serif", fontSize: '13px' }}
    >
      {/* Header Info */}
      <div className="text-left mb-3">
        <img src="/api/settings/logo" alt={settings?.companyName} style={{ height: '50px', width: 'auto' }} />
      </div>

      {/* Report Heading */}
      <div className="text-center mb-3">
        <h4
          id="client.name"
          className={`${!isReadOnly ? 'wysiwyg-editable' : ''} d-inline-block ${
            activeFieldId === 'client.name' ? 'smart-review-highlight' : ''
          }`}
          style={{ fontSize: '16px', fontWeight: 'bold', color: '#2f5496', margin: '0 auto' }}
          onClick={() => handleEditClick('client.name', 'client-name', 'Claimant Name', client)}
        >
          {clientName || (!isReadOnly && <span className="missing-data-badge">+ Add Claimant Name</span>)}
        </h4>
        <div style={{ fontSize: '13px', fontWeight: 'bold', margin: '2px 0' }}>
          {reportType === 'Progress' ? (
            <>
              Case Management Progress Report{' '}
              <span
                className={`${!isReadOnly ? 'wysiwyg-editable' : ''} px-1 text-primary`}
                onClick={() =>
                  handleEditClick(
                    'report.sequenceNumber',
                    'report-number',
                    'Progress Report Number',
                    caseReportModel?.sequenceNumber,
                  )
                }
                title={!isReadOnly ? 'Edit Report Number' : undefined}
              >
                {caseReportModel?.sequenceNumber || 1}
              </span>
            </>
          ) : (
            reportTitle
          )}
        </div>
        <div
          id="report.activityDates"
          className={`${!isReadOnly ? 'wysiwyg-editable' : ''} d-inline-block font-weight-bold ${
            activeFieldId === 'report.activityDates' ? 'smart-review-highlight' : ''
          }`}
          style={{ fontSize: '13px' }}
          onClick={() =>
            handleEditClick('report.activityDates', 'activity-dates', 'Activity Dates', {
              fromDateTimeUtc: caseReportModel?.fromDateTimeUtc,
              toDateTimeUtc: caseReportModel?.toDateTimeUtc,
            })
          }
        >
          {dateText || (isReadOnly ? '—' : <span className="missing-data-badge">+ Add Activity Dates</span>)}
        </div>
        <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#2f5496', marginTop: '4px' }}>
          {caseReportModel?.providerReportSubtext || settings?.reportHeaderSubtext}
        </div>
      </div>

      {/* Solid Rule & Correspondence Address */}
      <hr className="hard my-2" style={{ borderTop: '2px solid #000' }} />
      <div className="text-center mb-3" style={{ fontSize: '12px' }}>
        <strong>CORRESPONDENCE ADDRESS:</strong> {caseReportModel?.providerAddress || settings?.companyAddress}
      </div>
      <hr className="hard my-2" style={{ borderTop: '2px solid #000' }} />

      {/* Summary Table Grid */}
      <section className="mb-4">
        <table
          className="table table-bordered summary-grid-table"
          style={{ fontSize: '16px', width: '100%', borderCollapse: 'collapse', border: '1px solid #ccc' }}
        >
          <colgroup>
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
          </colgroup>
          <tbody>
            {/* Row 1: Claimant Name & Case Manager */}
            <tr>
              <td style={{ fontWeight: 'bold', padding: '6px', border: '1px solid #ccc' }}>Claimant Name:</td>
              <td
                className={`${!isReadOnly ? 'wysiwyg-editable' : ''} ${
                  activeFieldId === 'client.name' ? 'smart-review-highlight' : ''
                }`}
                style={{ padding: '6px', border: '1px solid #ccc' }}
                onClick={() => handleEditClick('client.name', 'client-name', 'Claimant Name', client)}
              >
                {clientName}
              </td>
              <td style={{ fontWeight: 'bold', padding: '6px', border: '1px solid #ccc' }}>Case Manager:</td>
              <td style={{ padding: '6px', border: '1px solid #ccc' }}>
                {caseManager ? `${caseManager.firstName} ${caseManager.lastName}` : 'N/A'}
              </td>
            </tr>
            {/* Row 2: DOB & CM Phone */}
            <tr>
              <td style={{ fontWeight: 'bold', padding: '6px', border: '1px solid #ccc' }}>DOB:</td>
              <td
                className={!isReadOnly ? 'wysiwyg-editable' : ''}
                style={{ padding: '6px', border: '1px solid #ccc' }}
                onClick={() => handleEditClick('client.birthdate', 'date', 'Date of Birth', client?.birthdate)}
              >
                {format.date(client?.birthdate) ||
                  (!isReadOnly && <span className="missing-data-badge">+ Add DOB</span>)}
              </td>
              <td style={{ fontWeight: 'bold', padding: '6px', border: '1px solid #ccc' }}>CM Phone:</td>
              <td style={{ padding: '6px', border: '1px solid #ccc' }}>
                {caseManager?.phoneNumber ? formatPhone(caseManager.phoneNumber) : 'N/A'}
              </td>
            </tr>
            {/* Row 3: DOI & Referral Date */}
            <tr>
              <td style={{ fontWeight: 'bold', padding: '6px', border: '1px solid #ccc' }}>DOI:</td>
              <td
                className={!isReadOnly ? 'wysiwyg-editable' : ''}
                style={{ padding: '6px', border: '1px solid #ccc' }}
                onClick={() => handleEditClick('case.injury.date', 'date', 'Date of Injury', caseData.injury?.date)}
              >
                {format.date(caseData.injury?.date) ||
                  (!isReadOnly && <span className="missing-data-badge">+ Add DOI</span>)}
              </td>
              <td style={{ fontWeight: 'bold', padding: '6px', border: '1px solid #ccc' }}>Referral date:</td>
              <td
                id="referral.date"
                className={`${!isReadOnly ? 'wysiwyg-editable' : ''} ${
                  activeFieldId === 'referral.date' ? 'smart-review-highlight' : ''
                }`}
                style={{ padding: '6px', border: '1px solid #ccc' }}
                onClick={() => handleEditClick('referral.date', 'date', 'Referral Date', referral?.date)}
              >
                {format.date(referral?.date) || (!isReadOnly && <span className="missing-data-badge">+ Add Date</span>)}
              </td>
            </tr>
            {/* Row 4: Claim # & Type of Referral */}
            <tr>
              <td style={{ fontWeight: 'bold', padding: '6px', border: '1px solid #ccc' }}>Claim #:</td>
              <td
                className={!isReadOnly ? 'wysiwyg-editable' : ''}
                style={{ padding: '6px', border: '1px solid #ccc' }}
                onClick={() => handleEditClick('referral.claimNumber', 'text', 'Claim Number', referral?.claimNumber)}
              >
                {referral?.claimNumber || (!isReadOnly && <span className="missing-data-badge">+ Add Claim #</span>)}
              </td>
              <td style={{ fontWeight: 'bold', padding: '6px', border: '1px solid #ccc' }}>Type of Referral:</td>
              <td
                className={!isReadOnly ? 'wysiwyg-editable' : ''}
                style={{ padding: '6px', border: '1px solid #ccc' }}
                onClick={() =>
                  handleEditClick('referral.referralType', 'referral-type', 'Type of Referral', referral?.referralType)
                }
              >
                {getReferralTypeDisplay(referral?.referralType) ||
                  (!isReadOnly && <span className="missing-data-badge">+ Add Type of Referral</span>)}
              </td>
            </tr>
            {/* Row 5: Employer & Job Title */}
            <tr>
              <td style={{ fontWeight: 'bold', padding: '6px', border: '1px solid #ccc' }}>Employer:</td>
              <td
                className={!isReadOnly ? 'wysiwyg-editable' : ''}
                style={{ padding: '6px', border: '1px solid #ccc' }}
                onClick={() => handleEditClick('case.employer', 'employer', 'Employer', employer)}
              >
                <div>
                  <strong>{employer.name || 'N/A'}</strong>
                </div>
                {employer.address?.line1 && <div>{employer.address.line1}</div>}
                {format.cityStateZip(employer.address) && <div>{format.cityStateZip(employer.address)}</div>}
                {employer.contact?.phone && (
                  <div>
                    <FAIcon name="phone" className="text-muted fa-sm mr-1" />
                    {formatPhone(employer.contact.phone)}
                  </div>
                )}
              </td>
              <td style={{ fontWeight: 'bold', padding: '6px', border: '1px solid #ccc' }}>Job Title:</td>
              <td
                className={!isReadOnly ? 'wysiwyg-editable' : ''}
                style={{ padding: '6px', border: '1px solid #ccc' }}
                onClick={() => handleEditClick('case.employer.occupation', 'text', 'Job Title', employer?.occupation)}
              >
                {employer.occupation || (!isReadOnly && <span className="missing-data-badge">+ Add Job Title</span>)}
              </td>
            </tr>
            {/* Row 6: Date of Hire & Spanned */}
            <tr>
              <td style={{ fontWeight: 'bold', padding: '6px', border: '1px solid #ccc' }}>Date of Hire:</td>
              <td
                className={!isReadOnly ? 'wysiwyg-editable' : ''}
                style={{ padding: '6px', border: '1px solid #ccc' }}
                onClick={() =>
                  handleEditClick('case.employer.dateOfHire', 'date', 'Date of Hire', employer?.dateOfHire)
                }
              >
                {format.date(employer?.dateOfHire) ||
                  (!isReadOnly && <span className="missing-data-badge">+ Add Date of Hire</span>)}
              </td>
              <td style={{ padding: '6px', border: '1px solid #ccc' }} colSpan="2"></td>
            </tr>
            {/* Row 7: Transportation Service & Translation Service */}
            <tr>
              <td style={{ fontWeight: 'bold', padding: '6px', border: '1px solid #ccc' }}>Transportation Service:</td>
              <td
                className={!isReadOnly ? 'wysiwyg-editable' : ''}
                style={{ padding: '6px', border: '1px solid #ccc' }}
                onClick={() =>
                  handleEditClick(
                    'case.transportation',
                    'transportation-list',
                    'Transportation Services',
                    allTransportation,
                  )
                }
              >
                {allTransportation.length > 0 ? allTransportation.map(t => t.providerName || t.name).join(', ') : 'N/A'}
              </td>
              <td style={{ fontWeight: 'bold', padding: '6px', border: '1px solid #ccc' }}>Translation Service:</td>
              <td
                className={!isReadOnly ? 'wysiwyg-editable' : ''}
                style={{ padding: '6px', border: '1px solid #ccc' }}
                onClick={() =>
                  handleEditClick(
                    'case.translationServices',
                    'translation-list',
                    'Translation Services',
                    allTranslation,
                  )
                }
              >
                {allTranslation.length > 0 ? allTranslation.map(t => t.providerName || t.name).join(', ') : 'N/A'}
              </td>
            </tr>
            {/* Row 8: ATTY: Respondent & ATTY: Claimant */}
            <tr>
              <td style={{ fontWeight: 'bold', padding: '6px', border: '1px solid #ccc' }}>ATTY: Respondent</td>
              <td
                className={!isReadOnly ? 'wysiwyg-editable' : ''}
                style={{ padding: '6px', border: '1px solid #ccc' }}
                onClick={() => handleEditClick('case.defendant', 'attorney', 'Respondent Attorney', caseData.defendant)}
              >
                {caseData.defendant?.name ? (
                  <div>
                    {respondentIndividual && (
                      <div>
                        <strong>{respondentIndividual}</strong>
                      </div>
                    )}
                    <div>{caseData.defendant.name}</div>
                    {caseData.defendant.address?.line1 && <div>{caseData.defendant.address.line1}</div>}
                    {format.cityStateZip(caseData.defendant.address) && (
                      <div>{format.cityStateZip(caseData.defendant.address)}</div>
                    )}
                    {caseData.defendant.contact?.phone && (
                      <div>
                        <FAIcon name="phone" className="text-muted fa-sm mr-1" />
                        {formatPhone(caseData.defendant.contact.phone)}
                      </div>
                    )}
                  </div>
                ) : (
                  'N/A'
                )}
              </td>
              <td style={{ fontWeight: 'bold', padding: '6px', border: '1px solid #ccc' }}>ATTY: Claimant</td>
              <td
                className={!isReadOnly ? 'wysiwyg-editable' : ''}
                style={{ padding: '6px', border: '1px solid #ccc' }}
                onClick={() => handleEditClick('case.plaintiff', 'attorney', 'Claimant Attorney', caseData.plaintiff)}
              >
                {caseData.plaintiff?.name ? (
                  <div id="client.address">
                    {claimantIndividual && (
                      <div>
                        <strong>{claimantIndividual}</strong>
                      </div>
                    )}
                    <div>{caseData.plaintiff.name}</div>
                    {caseData.plaintiff.address?.line1 && <div>{caseData.plaintiff.address.line1}</div>}
                    {format.cityStateZip(caseData.plaintiff.address) && (
                      <div>{format.cityStateZip(caseData.plaintiff.address)}</div>
                    )}
                    {caseData.plaintiff.contact?.phone && (
                      <div>
                        <FAIcon name="phone" className="text-muted fa-sm mr-1" />
                        {formatPhone(caseData.plaintiff.contact.phone)}
                      </div>
                    )}
                  </div>
                ) : (
                  'N/A'
                )}
              </td>
            </tr>
            {/* Row 9: Adjuster & Invoice # */}
            <tr>
              <td style={{ fontWeight: 'bold', padding: '6px', border: '1px solid #ccc' }}>Adjuster:</td>
              <td
                className={!isReadOnly ? 'wysiwyg-editable' : ''}
                style={{ padding: '6px', border: '1px solid #ccc' }}
                onClick={() =>
                  handleEditClick('referral.adjusterId', 'library-adjuster', 'Claims Adjuster', referral?.adjusterId)
                }
              >
                {adjuster ? (
                  <div>
                    <div>
                      <strong>{adjuster.name}</strong>
                    </div>
                    {adjuster.addressLine1 && <div>{adjuster.addressLine1}</div>}
                    {format.cityStateZip({
                      city: adjuster.addressCity,
                      state: adjuster.addressState,
                      zip: adjuster.addressZip,
                    }) && (
                      <div>
                        {format.cityStateZip({
                          city: adjuster.addressCity,
                          state: adjuster.addressState,
                          zip: adjuster.addressZip,
                        })}
                      </div>
                    )}
                    {adjuster.phone && (
                      <div>
                        <FAIcon name="phone" className="text-muted fa-sm mr-1" />
                        {formatPhone(adjuster.phone)}
                      </div>
                    )}
                    {adjuster.fax && <div>Fax: {formatPhone(adjuster.fax)}</div>}
                    {adjuster.email && <div>{adjuster.email}</div>}
                  </div>
                ) : (
                  'N/A'
                )}
              </td>
              <td style={{ fontWeight: 'bold', padding: '6px', border: '1px solid #ccc' }}>Invoice #:</td>
              <td
                className={!isReadOnly ? 'wysiwyg-editable' : ''}
                style={{ padding: '6px', border: '1px solid #ccc' }}
                onClick={() =>
                  handleEditClick('report.invoiceNumber', 'text', 'Invoice Number', caseReportModel?.invoiceNumber)
                }
              >
                {caseReportModel?.invoiceNumber ||
                  (caseReportModel?.noInvoiceNumberReason ? (
                    <span>
                      None{' '}
                      <span className="text-muted font-italic" style={{ fontSize: '0.9em' }}>
                        (Reason: {caseReportModel.noInvoiceNumberReason})
                      </span>
                    </span>
                  ) : !isReadOnly ? (
                    <span className="missing-data-badge">+ Add Invoice #</span>
                  ) : null)}
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* Description of Injury/Mechanism of Injury */}
      <section
        id="case.injury.description"
        className={`${!isReadOnly ? 'wysiwyg-editable' : ''} ${
          activeFieldId === 'case.injury.description' || activeFieldId?.includes('injury')
            ? 'smart-review-highlight'
            : ''
        }`}
        onClick={() =>
          handleEditClick(
            'case.injury.description',
            'injury-descriptions-list',
            'Description of Injury/Mechanism of Injury',
            rawInjuryDescriptions,
          )
        }
      >
        <header className="d-flex justify-content-between align-items-center">
          <span>Description of Injury/Mechanism of Injury:</span>
          {!isReadOnly && <FAIcon name="pen" className="text-primary text-muted fa-sm ml-2" />}
        </header>
        <div className="p-2">
          {allInjuryDescriptions.length > 0 ? (
            allInjuryDescriptions.map((item, idx) => {
              const cleanHtml = (item.description || '')
                .trim()
                .replace(/<\/p>\s*<p[^>]*>/gi, '<br />')
                .replace(/^<p[^>]*>/i, '')
                .replace(/<\/p>$/i, '')
              const providerText = (item.provider || '').trim()
              const dateStr =
                item.date && moment.utc(item.date).isValid() ? moment.utc(item.date).format('MM/DD/YYYY') : null
              return (
                <div
                  key={idx}
                  id={`injury.description.${idx}`}
                  className={idx < allInjuryDescriptions.length - 1 ? 'mb-2' : ''}
                >
                  <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cleanHtml) }} />
                  {(providerText || dateStr) && (
                    <span className="text-muted ml-2" style={{ fontStyle: 'italic', fontSize: '0.9em' }}>
                      - {[providerText, dateStr].filter(Boolean).join(' (')}
                      {providerText && dateStr ? ')' : ''}
                    </span>
                  )}
                </div>
              )
            })
          ) : !isReadOnly ? (
            <span className="missing-data-badge">+ Add Description of Injury</span>
          ) : null}
        </div>
      </section>

      <hr className="hard" />

      {/* Nurse Intake - new patient interview (Initial Reports Only) */}
      {isInitialReport && (
        <>
          <section>
            <header className="d-flex justify-content-between align-items-center">
              <span>Nurse Intake-new patient interview:</span>
              {!isReadOnly && <FAIcon name="pen" className="text-primary text-muted fa-sm ml-2" />}
            </header>
            <div
              id="report.nurseIntake"
              className={`${!isReadOnly ? 'wysiwyg-editable' : ''} p-2 ${
                activeFieldId === 'report.nurseIntake' ? 'smart-review-highlight' : ''
              }`}
              onClick={() =>
                handleEditClick(
                  'report.nurseIntake',
                  'rich-text',
                  'Nurse Intake-new patient interview',
                  caseReportModel?.nurseIntake,
                )
              }
            >
              {caseReportModel?.nurseIntake ? (
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(caseReportModel.nurseIntake) }} />
              ) : !isReadOnly ? (
                <span className="missing-data-badge">+ Add Nurse Intake Notes</span>
              ) : null}
            </div>
          </section>

          <hr className="hard" />
        </>
      )}

      {/* Overview of Appointments Attended/Current Medical Status */}
      <section>
        <header className="d-flex justify-content-between align-items-center">
          <span>Overview of Appointments Attended/Current Medical Status:</span>
          {!isReadOnly && Boolean(caseReportModel?.currentMedicalStatus) && (
            <FAIcon name="pen" className="text-primary text-muted fa-sm ml-2" />
          )}
        </header>
        <div
          id="report.currentMedicalStatus"
          className={`${!isReadOnly ? 'wysiwyg-editable' : ''} p-2 ${
            activeFieldId === 'report.currentMedicalStatus' ? 'smart-review-highlight' : ''
          }`}
          onClick={() =>
            handleEditClick(
              'report.currentMedicalStatus',
              'rich-text',
              'Overview of Appointments Attended/Current Medical Status',
              caseReportModel?.currentMedicalStatus,
            )
          }
        >
          {caseReportModel?.currentMedicalStatus ? (
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(caseReportModel.currentMedicalStatus),
              }}
            />
          ) : !isReadOnly ? (
            <span className="missing-data-badge">+ Add Narrative</span>
          ) : null}
        </div>
      </section>

      <hr className="hard" />

      {/* Symptoms & Diagnoses */}
      <section
        id="case.diagnoses"
        className={`${!isReadOnly ? 'wysiwyg-editable' : ''} ${
          activeFieldId === 'case.diagnoses' || activeFieldId?.startsWith('diagnosis') ? 'smart-review-highlight' : ''
        }`}
        onClick={() => handleEditClick('case.diagnoses', 'diagnoses-list', 'Symptoms and Diagnoses', allDiagnosis)}
      >
        <header className="d-flex justify-content-between align-items-center">
          <span>Symptoms and Diagnoses:</span>
          {!isReadOnly && <FAIcon name="pen" className="text-primary text-muted fa-sm ml-2" />}
        </header>
        {allDiagnosis.length === 0 ? (
          <ReportText>No Diagnoses Reported</ReportText>
        ) : (
          <ul style={{ listStyleType: 'none', paddingLeft: '1rem', margin: 0 }}>
            {allDiagnosis.map((item, idx) => (
              <li
                key={idx}
                id={`diagnosis.${idx}`}
                className="report-text"
                style={{ display: 'flex', alignItems: 'center', marginBottom: '0.25rem', lineHeight: '1.2' }}
              >
                <span style={{ marginRight: '1em', flexShrink: 0 }}>&bull;</span>
                <span>
                  {item.name}
                  {(item.physician || item.date) && (
                    <span className="text-muted ml-2" style={{ fontStyle: 'italic', fontSize: '0.9em' }}>
                      -{' '}
                      {[item.physician, item.date ? moment.utc(item.date).format('MM/DD/YYYY') : null]
                        .filter(Boolean)
                        .join(' (')}
                      {item.physician && item.date ? ')' : ''}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <hr className="hard" />

      {/* Medications and Allergies */}
      <section>
        <header>Medications and Allergies:</header>
        <div className="pl-3">
          <div
            id="case.medications"
            className={`${!isReadOnly ? 'wysiwyg-editable' : ''} p-2 rounded ${
              activeFieldId?.toLowerCase().includes('medicat') ? 'smart-review-highlight' : ''
            }`}
            onClick={() => handleEditClick('case.medications', 'medications-list', 'Medications', allMedications)}
          >
            <div className="d-flex justify-content-between align-items-center mb-1">
              <div className="font-weight-bold">Medications:</div>
              {!isReadOnly && <FAIcon name="pen" className="text-primary text-muted fa-sm" />}
            </div>
            {allMedications && allMedications.length > 0 ? (
              <ul className="pl-4 mb-2">
                {allMedications.map((med, idx) => {
                  const isDiscontinued = Boolean(med.discontinued || med.isDiscontinued || med.dateDiscontinued)
                  const isPreInjury = Boolean(med.preInjury || med.isPreInjury)
                  const isRelatedToInjury = Boolean(med.relatedToInjury)
                  return (
                    <li key={idx} className={isDiscontinued ? 'text-muted' : ''}>
                      <span className={isDiscontinued ? 'font-italic' : ''}>
                        {med.name}
                        {med.dosage ? ` (${med.dosage}${med.frequency ? `, ${med.frequency}` : ''})` : ''}
                        {med.reason ? ` - ${med.reason}` : ''}
                      </span>
                      {isRelatedToInjury && (
                        <span className="badge badge-info ml-2" style={{ fontSize: '0.72rem' }}>
                          Related to Injury
                        </span>
                      )}
                      {isPreInjury && (
                        <span className="badge badge-warning text-dark ml-2" style={{ fontSize: '0.72rem' }}>
                          Pre-injury
                        </span>
                      )}
                      {isDiscontinued && (
                        <span className="badge badge-secondary ml-2" style={{ fontSize: '0.72rem' }}>
                          Discontinued{med.dateDiscontinued ? `: ${med.dateDiscontinued}` : ''}
                        </span>
                      )}
                    </li>
                  )
                })}
              </ul>
            ) : (
              <div className="pl-4 mb-2 text-muted font-italic">• N/A</div>
            )}
          </div>

          <div
            id="case.allergies"
            className={`${!isReadOnly ? 'wysiwyg-editable' : ''} p-2 rounded mt-2 ${
              activeFieldId?.toLowerCase().includes('allerg') ? 'smart-review-highlight' : ''
            }`}
            onClick={() => handleEditClick('case.allergies', 'allergies-list', 'Allergies', allAllergies)}
          >
            <div className="d-flex justify-content-between align-items-center mb-1">
              <div className="font-weight-bold">Allergies:</div>
              {!isReadOnly && <FAIcon name="pen" className="text-primary text-muted fa-sm" />}
            </div>
            {allAllergies && allAllergies.length > 0 ? (
              <ul className="pl-4 mb-0">
                {allAllergies.map((allergy, idx) => (
                  <li key={idx}>
                    {allergy.name}
                    {allergy.reaction ? ` (Reaction: ${allergy.reaction})` : ''}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="pl-4 mb-0 text-muted font-italic">• N/A</div>
            )}
          </div>
        </div>
      </section>

      {allDiagnosticTests && allDiagnosticTests.length > 0 && (
        <>
          <hr className="hard" />
          {/* Diagnostic Tests */}
          <section
            id="case.tests"
            className={`${!isReadOnly ? 'wysiwyg-editable' : ''} ${
              activeFieldId?.toLowerCase().includes('test') ? 'smart-review-highlight' : ''
            }`}
          >
            <header>Diagnostic Tests:</header>
            <div className="pl-3">
              <ul className="pl-4 mb-0">
                {allDiagnosticTests.map((test, idx) => {
                  const testDateStr = test.date ? moment.utc(test.date).format('MM/DD/YYYY') : 'TBD'
                  return (
                    <li key={idx} className="mb-1">
                      <strong>
                        {testDateStr} - {test.type || test.testType || test.name}
                      </strong>
                      {test.results && test.results.replace(/<[^>]*>/g, '').trim() && (
                        <div className="text-muted" style={{ paddingLeft: '15px' }}>
                          <span>Results: </span>
                          <div
                            className="d-inline"
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(test.results) }}
                          />
                        </div>
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>
          </section>
        </>
      )}

      <hr className="hard" />

      {/* Providers and Appointments */}
      {(!isReadOnly || treatmentProviders.length > 0) && (
        <>
          <section
            id="case.treatmentProviders"
            className={`${!isReadOnly ? 'wysiwyg-editable' : ''} ${
              activeFieldId === 'case.treatmentProviders' || activeFieldId?.startsWith('provider')
                ? 'smart-review-highlight'
                : ''
            }`}
            onClick={() =>
              handleEditClick('case.treatmentProviders', 'treatment-providers', 'Providers', treatmentProviders)
            }
          >
            <header className="d-flex justify-content-between align-items-center">
              <span>Providers:</span>
              {!isReadOnly && <FAIcon name="pen" className="text-primary text-muted fa-sm ml-2" />}
            </header>
            {treatmentProviders.length > 0 ? (
              <div className="pl-3">
                {treatmentProviders.map((provider, idx) => {
                  const effectiveOrg =
                    provider.parentOrganization ||
                    (provider.companyName &&
                    provider.companyName.trim().toLowerCase() !== provider.name?.trim().toLowerCase()
                      ? provider.companyName
                      : null)

                  const providerHeading =
                    effectiveOrg && !provider.name?.toLowerCase().includes(effectiveOrg.toLowerCase())
                      ? `${provider.name} | ${effectiveOrg}`
                      : provider.name

                  const effectiveAddress = provider.address
                  const effectivePhone = provider.phone
                  const effectiveEmail = provider.email
                  const effectiveFax = provider.fax

                  return (
                    <div
                      key={idx}
                      id={`provider.${provider.id || provider.providerId || idx}`}
                      className={`mb-3 ${
                        activeFieldId === `provider.${provider.id || provider.providerId}` ||
                        (activeFieldId &&
                          provider.name &&
                          activeFieldId.toLowerCase().includes(provider.name.toLowerCase()))
                          ? 'smart-review-highlight p-2 rounded'
                          : ''
                      }`}
                    >
                      <div className="font-weight-bold">{providerHeading}</div>
                      {effectiveAddress && (
                        <div>
                          {typeof effectiveAddress === 'string' ? effectiveAddress : format.address(effectiveAddress)}
                        </div>
                      )}
                      {(effectivePhone || effectiveEmail || effectiveFax) && (
                        <div className="text-muted d-flex align-items-center flex-wrap" style={{ gap: '15px' }}>
                          {effectivePhone && (
                            <span className="d-flex align-items-center">
                              <FAIcon name="phone" className="text-muted fa-sm mr-1" />
                              {formatPhone(effectivePhone)}
                            </span>
                          )}
                          {effectiveEmail && (
                            <span className="d-flex align-items-center">
                              <FAIcon name="envelope" className="text-muted fa-sm mr-1" />
                              {effectiveEmail}
                            </span>
                          )}
                          {effectiveFax && (
                            <span className="d-flex align-items-center">
                              <FAIcon name="fax" className="text-muted fa-sm mr-1" />
                              {formatPhone(effectiveFax)}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            ) : !isReadOnly ? (
              <span className="missing-data-badge">No Providers Scheduled</span>
            ) : null}
          </section>

          <hr className="hard" />
        </>
      )}

      {/* Itemized Activities This Reporting Period */}
      <section
        id="report.activities"
        className={`mt-4 p-2 rounded ${activeFieldId === 'report.activities' ? 'smart-review-highlight' : ''}`}
      >
        <header>Itemized Activities This Reporting Period:</header>
        {itemizedRecords.length === 0 ? (
          <div
            className="text-center mt-3 font-italic"
            style={{
              fontSize: '14px',
              padding: '15px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              backgroundColor: '#f9f9f9',
            }}
          >
            No activities for the reporting period {fromDate.format('MM/DD/YYYY')} - {toDate.format('MM/DD/YYYY')}
          </div>
        ) : (
          <div className="table-responsive mt-2">
            <table className="table table-bordered table-sm" style={{ borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr className="bg-light text-center font-weight-bold">
                  <th style={{ width: '10%' }}>DATE</th>
                  <th style={{ width: '60%' }}>ACTIVITY</th>
                  <th style={{ width: '10%' }}>PROF. HOURS</th>
                  <th style={{ width: '10%' }}>MILES</th>
                  <th style={{ width: '10%' }}>EXTRA EXPENSE</th>
                </tr>
              </thead>
              <tbody>
                {sortedGroups.map((group, groupIdx) => {
                  const parentDateText =
                    group.parentDate === '9999-12-31' || !group.parentDate
                      ? ''
                      : moment(group.parentDate).isValid()
                      ? moment(group.parentDate).format('MM/DD/YYYY')
                      : ''
                  const parentTitleText = group.parentType
                    ? `${group.parentType}: ${group.parentTitle}`
                    : group.parentTitle

                  return (
                    <React.Fragment key={groupIdx}>
                      {/* Parent Row */}
                      {(() => {
                        const isClickableHeader =
                          !isReadOnly && (group.parentType === 'Appointment' || group.parentType === 'Task')

                        const handleAddActivity = e => {
                          e.stopPropagation()
                          if (!isClickableHeader) return
                          const prefix = group.parentType === 'Appointment' ? 'appt' : 'task'
                          const clickKey = `activity.new.${prefix}.${group.parentId}`
                          const defaultVal = {
                            date:
                              group.parentDate === '9999-12-31'
                                ? moment().format('YYYY-MM-DD')
                                : moment(group.parentDate).format('YYYY-MM-DD'),
                            type: '',
                            time: 0,
                            description: '',
                          }
                          handleEditClick(clickKey, 'activity', 'New Activity', defaultVal)
                        }

                        const handleParentRowClick = () => {
                          if (isReadOnly) return
                          if (group.parentType === 'Appointment' && group.parentId) {
                            const apptData = group.appointment || {
                              appointmentNumber: group.parentId,
                              title: group.parentTitle,
                            }
                            handleEditClick(
                              `appointment.${group.parentId}.details`,
                              'appointment-details',
                              `Edit Appointment - ${group.parentTitle || `Appointment #${group.parentId}`}`,
                              apptData,
                            )
                          }
                        }

                        return (
                          <tr
                            id={group.parentType === 'Appointment' ? `appointment.${group.parentId}` : undefined}
                            className={`bg-light font-weight-bold ${
                              !isReadOnly && group.parentType === 'Appointment' ? 'wysiwyg-editable' : ''
                            } ${
                              activeFieldId === `appointment.${group.parentId}` ||
                              activeFieldId === `appointment.${group.parentId}.details`
                                ? 'smart-review-highlight'
                                : ''
                            }`}
                            title={group.parentType === 'Appointment' ? 'Click to edit appointment details' : undefined}
                            onClick={handleParentRowClick}
                          >
                            <td className="text-center">{parentDateText}</td>
                            <td colSpan="4" className="text-left pl-2">
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  {parentTitleText}
                                  {group.parentType === 'Appointment' && !isReadOnly && (
                                    <FAIcon
                                      name="pen"
                                      className="text-primary text-muted fa-sm ml-2"
                                      title="Edit Appointment"
                                    />
                                  )}
                                </div>
                                {isClickableHeader && (
                                  <button
                                    type="button"
                                    className="btn btn-xs btn-outline-primary py-0 px-2 font-weight-bold"
                                    style={{ fontSize: '0.75rem' }}
                                    onClick={handleAddActivity}
                                    title="Add new activity to this appointment"
                                  >
                                    <i className="fas fa-plus mr-1" />
                                    Add Activity
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        )
                      })()}
                      {/* Child Rows */}
                      {group.items.map((ir, itemIdx) => {
                        const itemDateText = ir.date ? moment(ir.date).format('MM/DD/YYYY') : ''

                        let clickKey = ''
                        let clickType = ''
                        let clickTitle = ''
                        let clickVal = null

                        if (ir.type === 'activity') {
                          const apptPart = ir.parentId && ir.parentType === 'Appointment' ? `.appt.${ir.parentId}` : ''
                          const taskPart = ir.parentId && ir.parentType === 'Task' ? `.task.${ir.parentId}` : ''
                          clickKey = `activity.${ir.recordId}${apptPart}${taskPart}`
                          clickType = 'activity'
                          clickTitle = `Activity - ${ir.originalItem.type || 'Details'}`
                          clickVal = ir.originalItem
                        } else {
                          const apptPart = ir.parentId && ir.parentType === 'Appointment' ? `.appt.${ir.parentId}` : ''
                          const taskPart = ir.parentId && ir.parentType === 'Task' ? `.task.${ir.parentId}` : ''
                          clickKey = `expense.${ir.recordId}${apptPart}${taskPart}`
                          clickType = 'expense'
                          clickTitle = `Expense - ${ir.originalItem.type || 'Details'}`
                          clickVal = ir.originalItem
                        }

                        const isHoursZero = (ir.hours || 0) <= 0
                        const isDescEmpty = !ir.description && !ir.originalItem.type

                        return (
                          <tr
                            key={itemIdx}
                            id={ir.type === 'activity' ? `activity.${ir.recordId}` : `expense.${ir.recordId}`}
                            className={`${!isReadOnly ? 'wysiwyg-editable' : ''} ${
                              activeFieldId === `activity.${ir.recordId}` || activeFieldId === `expense.${ir.recordId}`
                                ? 'smart-review-highlight'
                                : ''
                            } ${isHoursZero && ir.type === 'activity' && !isReadOnly ? 'table-warning' : ''}`}
                            onClick={() => handleEditClick(clickKey, clickType, clickTitle, clickVal)}
                          >
                            <td className="text-center">{itemDateText}</td>
                            <td className="text-left pl-3">
                              {ir.originalItem.type && <strong>{ir.originalItem.type}: </strong>}
                              {isDescEmpty ? (
                                !isReadOnly ? (
                                  <span className="text-danger font-italic font-weight-bold">
                                    [Missing description - click to edit or delete]
                                  </span>
                                ) : null
                              ) : (
                                (ir.description || '').replace(/^<p>/i, '').replace(/<\/p>$/i, '')
                              )}
                            </td>
                            <td className="text-right pr-2">
                              {ir.hours > 0 ? (
                                roundTwoDecimals(ir.hours)
                              ) : ir.type === 'activity' ? (
                                <span
                                  className="badge badge-warning text-dark font-weight-bold"
                                  title="No billable time recorded"
                                >
                                  0.00 hrs (Incomplete)
                                </span>
                              ) : (
                                ''
                              )}
                            </td>
                            <td className="text-right pr-2">{ir.miles > 0 ? roundTwoDecimals(ir.miles) : ''}</td>
                            <td className="text-right pr-2">
                              {ir.expenseAmount > 0 ? `$${ir.expenseAmount.toFixed(2)}` : ''}
                            </td>
                          </tr>
                        )
                      })}
                    </React.Fragment>
                  )
                })}
              </tbody>
              <tfoot>
                <tr className="font-weight-bold bg-light">
                  <td></td>
                  <td className="text-left pl-2">TOTAL</td>
                  <td className="text-right pr-2">{totalHours > 0 ? totalHours : ''}</td>
                  <td className="text-right pr-2">{totalMiles > 0 ? totalMiles : ''}</td>
                  <td className="text-right pr-2">{totalExpenses > 0 ? `$${totalExpenses.toFixed(2)}` : ''}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>

      <hr className="hard" />

      {/* Work Status */}
      <section className="mt-4">
        <header className="d-flex justify-content-between align-items-center">
          <span>Work Status:</span>
          {!isReadOnly && <FAIcon name="pen" className="text-primary text-muted fa-sm ml-2" />}
        </header>
        <div
          id="case.status"
          className={`${!isReadOnly ? 'wysiwyg-editable' : ''} p-2 ${
            activeFieldId === 'case.status' || activeFieldId?.includes('work') ? 'smart-review-highlight' : ''
          }`}
          onClick={() => handleEditClick('case.status', 'work-status', 'Work Status', status)}
        >
          <WorkStatusContent workStatus={status} isReadOnly={isReadOnly} />
        </div>
      </section>

      <hr className="hard" />

      {/* Maximum Medical Improvement (MMI) */}
      {(!isReadOnly || latestMMI) && (
        <>
          <section
            id="case.mmi"
            className={`mt-4 ${!isReadOnly ? 'wysiwyg-editable' : ''} ${
              activeFieldId === 'case.mmi' || activeFieldId?.includes('mmi') ? 'smart-review-highlight' : ''
            }`}
            onClick={() => handleEditClick('case.mmi', 'mmi', 'MMI Status', caseData.mmi)}
          >
            <header className="d-flex justify-content-between align-items-center">
              <span>Maximum Medical Improvement (MMI):</span>
              {!isReadOnly && <FAIcon name="pen" className="text-primary text-muted fa-sm ml-2" />}
            </header>
            <div className="pl-3">
              {latestMMI ? (
                <div>
                  {latestMMI.date ? (
                    <div className="mb-2">
                      <strong>{latestMMI.isActual ? 'Actual MMI Date' : 'Estimated MMI Date'}:</strong>{' '}
                      {format.date(latestMMI.date)}
                    </div>
                  ) : (
                    latestMMI.isActual && (
                      <div className="mb-2">
                        <strong>Actual MMI Date:</strong> TBD
                      </div>
                    )
                  )}
                  {latestMMI.narrative ? (
                    <div>
                      <strong>MMI Narrative:</strong>
                      <div
                        className="mmi-narrative-content"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(latestMMI.narrative) }}
                      />
                    </div>
                  ) : null}
                </div>
              ) : !isReadOnly ? (
                <span className="missing-data-badge">+ Add MMI Status</span>
              ) : null}
            </div>
          </section>
        </>
      )}
    </div>
  )
}

function WorkStatusContent({ workStatus, isReadOnly }) {
  if (!workStatus) return !isReadOnly ? <span className="missing-data-badge">+ Add Work Status Details</span> : null

  const history = workStatus.history || []
  const latest = history[history.length - 1] || workStatus

  const hasDate = latest.date != null
  const hasProvider = latest.treatmentProvider && latest.treatmentProvider.trim().length > 0
  const hasStatus = latest.workStatus && latest.workStatus.trim().length > 0

  if (!hasDate && !hasProvider && !hasStatus) {
    return !isReadOnly ? <span className="missing-data-badge">+ Add Work Status Details</span> : null
  }

  return (
    <div>
      {hasDate && (
        <div>
          <strong>Date Reported:</strong> {format.date(latest.date)}
        </div>
      )}
      {hasProvider && (
        <div>
          <strong>Treatment Provider:</strong> {latest.treatmentProvider}
        </div>
      )}
      {hasStatus && (
        <div>
          <strong>Work Status:</strong> {latest.workStatus || latest.status}
        </div>
      )}
    </div>
  )
}
